import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HeartPulse } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { tooltipStyle } from "./oversiktConstants";
import { dec, formatNumber } from "@/lib/formatters";

const n1 = (v) => (v == null ? "—" : dec(v, 1));

const MAAL = [
  { key: "barn_per_arsverk", label: "Barn per årsverk i barnehage", unit: "barn", testId: "barn-per-arsverk" },
  { key: "elevar_per_laerararsverk", label: "Elevar per lærarårsverk", unit: "elevar", testId: "elevar-per-arsverk" },
  { key: "brukarar_per_arsverk_omsorg", label: "Brukarar per årsverk i omsorgstenesta", unit: "brukarar", testId: "brukarar-per-arsverk", desimalar: 2 },
  { key: "dekning_1_5", label: "Barnehagedekning 1–5 år", unit: "%", testId: "barnehage" },
  { key: "elevar_per_skule", label: "Elevar per kommunale skule", unit: "elevar", testId: "skule" },
  { key: "heildogn_per_100_eldre", label: "Sjukeheim og omsorgsbustad per 100 over 80 år", unit: "plassar", testId: "sjukeheim" },
  { key: "kultur_per_100_barn", label: "Kulturskuleplassar per 100 barn 6–18 år", unit: "plassar", testId: "kulturskule" },
];

const TjenesterCompareChart = ({ data, colors }) => {
  const rows = Object.values(data?.kommuner || {});
  if (rows.length === 0) return null;
  const land = data.land;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-tjenester-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-[#103766]" />
          Tenester til barn, unge og eldre {rows[0].year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Brukarar per årsverk i barnehage, skule og omsorg, og dekninga på plassar og einingar side om side. Stipla linje er landssnittet.
        </p>
      </CardHeader>
      <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {MAAL.map((m) => {
          const bars = rows.filter((r) => r.latest?.[m.key] != null).map((r) => ({ name: r.name, verdi: r.latest[m.key] }));
          if (bars.length === 0) return null;
          return (
            <div key={m.key}>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">{m.label}</p>
              <div className="h-[240px]" data-testid={`compare-tjeneste-${m.testId}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bars} barCategoryGap="35%" margin={{ top: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => dec(v, 0)} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${dec(v, m.desimalar ?? 1)} ${m.unit}`, m.label]} />
                    {land?.[m.key] != null && (
                      <ReferenceLine y={land[m.key]} stroke="#4B647F" strokeDasharray="4 4"
                        label={{ value: `Landet ${dec(land[m.key], m.desimalar ?? 1)}`, position: "insideTopRight", fontSize: 11, fill: "#4B647F" }} />
                    )}
                    <Bar dataKey="verdi" radius={[4, 4, 0, 0]}>
                      {bars.map((b, i) => <Cell key={b.name} fill={colors[i % colors.length]} />)}
                      <LabelList dataKey="verdi" position="top" formatter={(v) => dec(v, m.desimalar ?? 1)} fontSize={12} fill="#1F2A36" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
        <div className="lg:col-span-2 overflow-x-auto">
          <table className="w-full text-xs" data-testid="compare-tjenester-table">
            <thead>
              <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                <th className="py-2 pr-3 text-left font-medium">Kommune</th>
                <th className="py-2 px-3 text-right font-medium">Barn per årsverk</th>
                <th className="py-2 px-3 text-right font-medium">Elevar per lærarårsverk</th>
                <th className="py-2 px-3 text-right font-medium">Brukarar per årsverk omsorg</th>
                <th className="py-2 px-3 text-right font-medium">Barnehagedekning</th>
                <th className="py-2 px-3 text-right font-medium">Elevar per skule</th>
                <th className="py-2 px-3 text-right font-medium">Sjukeheimsplassar</th>
                <th className="py-2 px-3 text-right font-medium">Omsorgsbustader</th>
                <th className="py-2 px-3 text-right font-medium">Per 100 over 80 år</th>
                <th className="py-2 px-3 text-right font-medium">Kulturskuleplassar</th>
                <th className="py-2 px-3 text-right font-medium">Skular for same storleik 2050</th>
                <th className="py-2 px-3 text-right font-medium">Sjukeheim og bustad manglar 2050</th>
                <th className="py-2 pl-3 text-right font-medium">Barnehage manglar 2050</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.name} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`compare-tjenester-row-${i}`}>
                  <td className="py-2 pr-3 font-medium" style={{ color: colors[i % colors.length] }}>{r.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{n1(r.latest.barn_per_arsverk)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{n1(r.latest.elevar_per_laerararsverk)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {r.latest.brukarar_per_arsverk_omsorg == null ? "—" : dec(r.latest.brukarar_per_arsverk_omsorg, 2)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{n1(r.latest.dekning_1_5)} %</td>
                  <td className="py-2 px-3 text-right tabular-nums">{n1(r.latest.elevar_per_skule)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatNumber(r.latest.sjukeheimsplassar)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatNumber(r.latest.omsorgsbustader)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{n1(r.latest.heildogn_per_100_eldre)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatNumber(r.latest.kulturskuleplassar)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {r.skulebolge ? `${n1(r.skulebolge.siste.skular_for_same_storleik)} av ${formatNumber(r.skulebolge.skular)}` : "—"}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {r.eldrebolge?.siste ? `${formatNumber(r.eldrebolge.siste.mangel)} plassar` : "—"}
                  </td>
                  <td className="py-2 pl-3 text-right tabular-nums">
                    {r.barnehagebolge?.siste ? `${formatNumber(r.barnehagebolge.siste.mangel)} plassar` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#8AA4BF] mt-2">
            «Manglar 2050» er kor mange plassar kommunen må byggje for å ha same dekning som i dag – målt mot innbyggjarar 80 år og over og mot barn
            1–5 år – med SSB sitt hovudalternativ for befolkningsframskriving (14288). Negative tal tyder at kommunen får plassar til overs.
            «Skular for same storleik 2050» er kor mange skular kommunen treng for å halde dagens elevtal per skule, mot talet på skular i dag.
            Omsorgsbustader er bebuarar i bustader kommunen disponerer til pleie- og omsorgsformål (SSB 04469), i alle aldrar.
            «Barn per årsverk» er barn korrigert for alder per årsverk til grunnbemanning (norma er 6,0), «elevar per lærarårsverk» er elevar i
            kommunale skular per avtalte lærarårsverk, og «brukarar per årsverk omsorg» er rekna om frå SSB sitt nøkkeltal «årsverk per brukar».
            Kjelder: KOSTRA 13502, SSB 14522, SSB 11971, SSB 11981, KOSTRA 11875, KOSTRA 12209, SSB 11645, SSB 04469 og 14523.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TjenesterCompareChart;
